import { Star } from "lucide-react";
import { useTestimonials } from "@/hooks/useTestimonials";

function Stars({ rating }: { rating: number }) {
  return (
    <div className="flex gap-0.5" aria-label={`${rating} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`h-3.5 w-3.5 ${i < rating ? "fill-amber-400 text-amber-400" : "text-border"}`}
          strokeWidth={1.5}
        />
      ))}
    </div>
  );
}

interface Props {
  /** Max testimonials to show. Wizard uses 1 (slim); checkout uses 2. */
  limit?: number;
  className?: string;
}

/**
 * Slim social-proof strip for the conversion flow — testimonials at the wizard
 * and checkout, not just the homepage (#256). Reads the same DB rows the
 * marketing site shows and self-hides when none exist (mirrors marketing's
 * ClientFeedback after #259 removed the fabricated fallback copy).
 */
export function TestimonialStrip({ limit = 2, className = "" }: Props) {
  const { data } = useTestimonials();
  const items = (data ?? []).slice(0, limit);
  if (items.length === 0) return null;

  return (
    <aside
      className={`rounded-lg border border-border bg-muted/30 p-4 ${className}`}
      aria-label="Client testimonials"
    >
      <ul className="space-y-3">
        {items.map((t) => (
          <li key={t.id} className="text-sm">
            {t.rating ? <Stars rating={t.rating} /> : null}
            <blockquote className="mt-1.5 leading-relaxed text-foreground/90">
              &ldquo;{t.quote}&rdquo;
            </blockquote>
            <p className="mt-1 text-xs text-muted-foreground">
              {t.author_name}
              {t.author_title ? ` · ${t.author_title}` : ""}
            </p>
          </li>
        ))}
      </ul>
    </aside>
  );
}
